import "./ControlSection.css";

import {
  clearResultMapSite,
  fetchResultMapFeatures,
  fullResetResultMap,
  setSelectedContract,
} from "../../../actions/resultMap";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ANALYTICS_EVENTS } from "../../../constants";
import { Button } from "reactstrap";
import { ExportSection } from "../exportSection/exportSection";
import { FloatingCard } from "../../../components/floatingCard/FloatingCard";
import PropTypes from "prop-types";
import { CfSelect as Select } from "../../../components/CfSelect";
import { SimpleMode } from "./SimpleMode";
import { UploadYourOwnData } from "../uploadYourOwnData/UploadYourOwnData";
import { hasAnalyticsRole } from "../../../users/rolesUtil";
import { hasBackofficeAccess } from "../../../services/roles";
import { selectFilteredFeatures } from "../../../selectors/resultMap";
import { selectSortedTrials } from "../../../selectors/user";
import { uniqueArrayOfObject } from "../../../utils";
import { useClickAway } from "react-use";
import { useKeyboardShortcut } from "../../../hooks/useKeyboardShortcut";
import { useMapBulkExport } from "../../../WorldMap/useMapBulkExport";
import { useTracking } from "../../../analytics";

export const ControlSection = ({
  alwaysExpanded = false,
  onDownload,
  showFilteredExport = false,
}) => {
  const sortedUserTrials = useSelector(selectSortedTrials);
  const user = useSelector(({ user }) => user);
  const [trial, trialDate, refreshing, selectedContract, features] =
    useSelector(({ resultMap }) => [
      resultMap.trial,
      resultMap.trial_date,
      resultMap.refreshing,
      resultMap.selectedContract,
      resultMap.features,
    ]);
  const filteredFeatures = useSelector(selectFilteredFeatures);

  const { trackEvent } = useTracking();
  const dispatch = useDispatch();
  const history = useHistory();

  const bulkExport = useMapBulkExport(
    showFilteredExport && filteredFeatures.length > 0 && (
      <ExportSection
        trial={trial}
        selectedDate={trialDate}
        features={filteredFeatures}
        totalCount={features.length}
      />
    )
  );

  // Pre filtered
  const filteredContracts = useMemo(
    () => user.contracts.filter(({ roles }) => hasAnalyticsRole(roles)),
    [user.contracts]
  );

  // Calculate companies from user contracts
  const companies = uniqueArrayOfObject(
    filteredContracts.map((contract) => contract.company),
    "id"
  );
  const companiesOptions = companies
    .map((company) => ({
      label: company.name,
      value: company.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const [selectedCompany, setSelectedCompany] = useState();

  const companyValue =
    selectedCompany !== undefined
      ? selectedCompany
      : companiesOptions.find(
          ({ value }) => value === selectedContract?.company?.id
        ) ?? null;
  const [simpleMode, setSimpleMode] = useState(true);

  const componentRef = useRef();
  useClickAway(componentRef, () => {
    if (!alwaysExpanded && trial && !simpleMode) setSimpleMode(true);
  });

  // Takes over the "Quit this site" cross of SimpleMode when it is not shown.
  const resetWorldMap = () => {
    dispatch(clearResultMapSite());
    const currentPath = location.pathname;
    if (currentPath.startsWith("/map")) history.push("/map");
  };

  const selectSkin = alwaysExpanded
    ? { className: "cf-rs", classNamePrefix: "cf-rs" }
    : {
        className: "control-section__select",
        classNamePrefix: "control-section__select",
      };

  const location = useLocation();
  // Default set to simple mode on trial change
  const [prevTrial, setPrevTrial] = useState(null);
  if (prevTrial !== trial) {
    if (!alwaysExpanded) setSimpleMode(true);
    setPrevTrial(trial);
  }

  const [uploadIsOpen, setUploadIsOpen] = useState(false);

  // If only one contract, Select it by default
  useEffect(() => {
    if (filteredContracts.length === 1 && !selectedContract)
      dispatch(setSelectedContract(filteredContracts[0]));
  }, [dispatch, filteredContracts, selectedContract]);

  const siteOptions = sortedUserTrials.filter((userTrial) =>
    selectedContract?.sites.map(({ id }) => id).includes(userTrial.id)
  );

  const [autoSelectFor, setAutoSelectFor] = useState(null);
  const [prevContractId, setPrevContractId] = useState(selectedContract?.id);
  if (prevContractId !== selectedContract?.id) {
    setPrevContractId(selectedContract?.id);
    setAutoSelectFor(alwaysExpanded ? selectedContract?.id ?? null : null);
  }

  useEffect(() => {
    if (autoSelectFor == null) return;
    // A site already open (from the URL, or the one picked here) disarms it.
    if (trial) {
      setAutoSelectFor(null);
      return;
    }

    if (
      refreshing ||
      user.trialsLoading ||
      user.contractsLoading ||
      siteOptions.length === 0
    )
      return;
    setAutoSelectFor(null);
    if (siteOptions.length === 1)
      dispatch(
        fetchResultMapFeatures(
          siteOptions[0],
          siteOptions[0].trial_dates.at(-1)
        )
      );
  }, [
    autoSelectFor,
    dispatch,
    refreshing,
    siteOptions,
    trial,
    user.trialsLoading,
    user.contractsLoading,
  ]);

  useKeyboardShortcut("u", () => setUploadIsOpen(true));

  return (
    <div className="powerdash-component control-section" ref={componentRef}>
      {/* Both flags: the map only dispatches fetchUserTrials, which raises
          trialsLoading alone, and reading contractsLoading by itself announced
          "No campaign available" for the whole fetch. A refetch over the persisted
          list keeps showing it. */}
      {(user.contractsLoading || user.trialsLoading) &&
      !trial &&
      sortedUserTrials.length === 0 ? (
        <div className="text-center font-style-italic">
          We are loading your campaigns...
        </div>
      ) : sortedUserTrials.length === 0 ? (
        <div className="text-center font-style-italic">
          No campaign available
        </div>
      ) : !alwaysExpanded && simpleMode && trial ? (
        <SimpleMode
          onClick={() => setSimpleMode(false)}
          trial={trial}
          refreshing={refreshing}
        />
      ) : (
        <>
          {!alwaysExpanded && filteredContracts.length === 1 && (
            <span className="font-weight-bold">
              <i className="fa-solid fa-file-signature" />{" "}
              {filteredContracts[0].name}
            </span>
          )}
          {(alwaysExpanded || companiesOptions.length > 1) && (
            <Select
              isClearable
              options={companiesOptions}
              value={companyValue}
              onChange={(value) => {
                setSelectedCompany(value);
                if (!value) {
                  dispatch(fullResetResultMap());
                  if (location.pathname.startsWith("/map"))
                    history.push("/map");
                  return;
                }
                if (trial && trial.company_id !== value.value) resetWorldMap();
                const companyContracts = filteredContracts.filter(
                  ({ company }) => company.id === value.value
                );
                if (companyContracts.length === 1)
                  dispatch(setSelectedContract(companyContracts[0]));
                else if (selectedContract?.company?.id !== value.value)
                  dispatch(setSelectedContract(null));
              }}
              isDisabled={refreshing}
              placeholder="Filter by company"
              {...selectSkin}
            />
          )}
          {(alwaysExpanded || filteredContracts.length > 1) && (
            <Select
              isClearable={!alwaysExpanded && !trial}
              options={(companyValue
                ? filteredContracts.filter(
                    ({ company }) => company.id === companyValue.value
                  )
                : filteredContracts
              )
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))}
              value={selectedContract}
              id="contract"
              name="contract"
              onChange={(selectedOption) => {
                if (alwaysExpanded && selectedOption)
                  setSelectedCompany(undefined);
                if (trial && trial.contract_id !== selectedOption?.id)
                  resetWorldMap();
                dispatch(setSelectedContract(selectedOption));
              }}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ id }) => id.toString()}
              isDisabled={refreshing}
              placeholder="Select campaign"
              {...selectSkin}
            />
          )}
          <Select
            options={siteOptions}
            value={trial}
            id="trial"
            name="trial"
            isClearable={alwaysExpanded}
            onChange={(newTrial) => {
              if (!newTrial) {
                resetWorldMap();
                return;
              }
              dispatch(
                fetchResultMapFeatures(newTrial, newTrial.trial_dates.at(-1))
              );
            }}
            getOptionLabel={({ display_name }) => display_name}
            getOptionValue={({ id }) => id.toString()}
            isDisabled={refreshing || selectedContract == null}
            placeholder="Select site"
            {...selectSkin}
          />
        </>
      )}
      {trial && (
        <>
          <div className="d-flex gap-1">
            {hasBackofficeAccess(user.self) && (
              <Button
                data-tooltip-id="tooltip"
                data-tooltip-content="Open in administration"
                size="sm"
                onClick={() =>
                  history.push(
                    `/administration/company/${trial.company_id}/contract/${trial.contract_id}`
                  )
                }
              >
                <i className="fa-solid fa-lg fa-gears" />
              </Button>
            )}
            {/* onDownload: the map SiteDrawer keeps the export drawer mounted
                when it closes, so it brings its own. */}
            <Button
              // The bulk export also covers the campaign's other sites.
              disabled={!onDownload && !bulkExport.canExport}
              color="success"
              className="flex-grow-1 text-ellipsis"
              size="sm"
              onClick={onDownload || bulkExport.open}
            >
              <i className="fa-solid fa-download" /> Download results
            </Button>
          </div>
          {features.length > 0 && (
            <>
              <Button
                color="info"
                className="w-100"
                onClick={() => {
                  setUploadIsOpen(!uploadIsOpen);
                  trackEvent(ANALYTICS_EVENTS.OPEN_UPLOAD_YOUR_OWN_DATA);
                }}
                size="sm"
              >
                <i className="fa-solid fa-upload me-1" />
                Upload my custom data
              </Button>
              {uploadIsOpen && (
                <FloatingCard hide={() => setUploadIsOpen(false)}>
                  <UploadYourOwnData
                    trial={trial}
                    setUploadIsOpen={setUploadIsOpen}
                  />
                </FloatingCard>
              )}
            </>
          )}
          {!onDownload && bulkExport.drawer}
        </>
      )}
    </div>
  );
};

ControlSection.propTypes = {
  // Keep the company, campaign and site selects on screen.
  alwaysExpanded: PropTypes.bool,
  onDownload: PropTypes.func,
  // Offer the filtered plots in the download drawer. Only where the filters
  // are on screen: elsewhere they come from another page, unseen.
  showFilteredExport: PropTypes.bool,
};
